import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUploadUrl, uploadFileToS3, dispatchLead } from "../api";
import { useLanguage } from "../LanguageContext";

const JOB_SIZES: Record<string, { label: string; desc: string }[]> = {
  Electrical: [
    { label: "Small", desc: "1-2 outlets or switches" },
    { label: "Medium", desc: "Panel work or 3-5 fixtures" },
    { label: "Large", desc: "Full rewire or major install" },
  ],
  Plumbing: [
    { label: "Small", desc: "Single fixture or minor repair" },
    { label: "Medium", desc: "Multiple fixtures or drain work" },
    { label: "Large", desc: "Full installation or major repair" },
  ],
  Cleaning: [
    { label: "Studio/1 Bedroom", desc: "Up to 700 sq ft" },
    { label: "2 Bedrooms", desc: "700-1200 sq ft" },
    { label: "3 Bedrooms", desc: "1200-1800 sq ft" },
    { label: "4+ Bedrooms", desc: "1800+ sq ft" },
  ],
  Outdoor: [
    { label: "Small", desc: "Under 500 sq ft" },
    { label: "Medium", desc: "500-1500 sq ft" },
    { label: "Large", desc: "1500+ sq ft" },
  ],
  Moving: [
    { label: "Studio/1 Bedroom", desc: "Few items or small space" },
    { label: "2 Bedrooms", desc: "Medium move" },
    { label: "3 Bedrooms", desc: "Large move" },
    { label: "4+ Bedrooms", desc: "Full home move" },
  ],
  Painting: [
    { label: "1 Room", desc: "Single room or accent wall" },
    { label: "2-3 Rooms", desc: "Multiple rooms" },
    { label: "Whole Home", desc: "Full interior or exterior" },
  ],
};

const DEFAULT_SIZES = [
  { label: "Small", desc: "Minor work or quick fix" },
  { label: "Medium", desc: "Standard job" },
  { label: "Large", desc: "Major project" },
];

const URGENCY_OPTIONS = [
  { value: "NORMAL", label: "Flexible", desc: "Within 1-2 weeks", icon: "📅" },
  { value: "HIGH", label: "Soon", desc: "Within 2-3 days", icon: "⚡" },
  { value: "LOW", label: "Planning ahead", desc: "1+ month away", icon: "🗓️" },
];

export default function QuoteRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();

  const proId = searchParams.get("proId") || "";
  const category = searchParams.get("category") || "";
  const proName = searchParams.get("name") || "a professional";

  const [step, setStep] = useState(0);
  const [jobSize, setJobSize] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [urgency, setUrgency] = useState("NORMAL");
  const [privateNotes, setPrivateNotes] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const sizes = JOB_SIZES[category] || DEFAULT_SIZES;

  const STEPS = [
    { label: "Job Details", icon: "📋" },
    { label: "Location & Time", icon: "📍" },
    { label: "Photos", icon: "📸" },
  ];

  function validateStep() {
    if (step === 0 && (!jobSize || !description.trim())) return "Please select job size and describe the work";
    if (step === 1 && (!address.trim() || !preferredDate)) return "Please enter your address and preferred date";
    return "";
  }

  async function handleNext() {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    if (step < STEPS.length - 1) { setStep(step + 1); return; }
    await handleSubmit();
  }

  async function handleSubmit() {
    setLoading(true); setError("");
    try {
      const photoUrls: string[] = [];
      for (const photo of photos) {
        const { uploadUrl, fileUrl } = await getUploadUrl(photo.name, photo.type, "quote-photos");
        await uploadFileToS3(uploadUrl, photo);
        photoUrls.push(fileUrl);
      }
      const result = await dispatchLead({
        category, jobSize, description, address,
        scheduledDate: preferredDate,
        photoUrls, privateNotes,
        specificProId: proId || undefined,
        urgency
      });
      if (result.dispatchType === "smart" && result.dispatchId) {
        navigate(`/select-pro?dispatchId=${result.dispatchId}`);
      } else {
        setSuccess(true);
      }
    } catch { setError("Failed to send quote request. Please try again."); }
    finally { setLoading(false); }
  }

  if (success) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
      <div className="text-center animate-slide-up max-w-sm">
        <div className="w-20 h-20 bg-teal-light rounded-full flex items-center justify-center text-4xl mx-auto mb-4">✅</div>
        <h1 className="font-display text-2xl font-bold text-navy mb-2">Request Sent!</h1>
        <p className="text-sm text-gray-500 mb-6">
          Your quote request has been sent to <span className="font-bold text-navy">{proName}</span>.
          They'll respond within 1 hour.
        </p>
        <div className="space-y-3">
          <button onClick={() => navigate("/bookings")} className="btn btn-primary btn-full">View My Bookings</button>
          <button onClick={() => navigate("/explore")} className="btn btn-secondary btn-full">Browse More Pros</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-5 pt-5 pb-4 sticky top-0 z-20">
        <button onClick={() => step === 0 ? navigate(-1) : setStep(step - 1)}
          className="text-gray-400 text-sm font-semibold mb-3 flex items-center gap-1">
          ← {step === 0 ? "Back" : "Previous"}
        </button>

        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-navy rounded-xl flex items-center justify-center text-xl">
            {STEPS[step].icon}
          </div>
          <div>
            <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">
              Step {step + 1} of {STEPS.length}
            </div>
            <h1 className="font-display text-xl font-bold text-navy">{STEPS[step].label}</h1>
          </div>
        </div>

        {/* Progress */}
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s.label} className={"flex-1 h-1.5 rounded-full transition-all duration-300 " +
              (i <= step ? "bg-mouna-blue" : "bg-gray-200")}/>
          ))}
        </div>
      </div>

      {/* Pro banner if direct booking */}
      {proId && (
        <div className="bg-blue-pale px-5 py-3 flex items-center gap-3">
          <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white text-xs font-bold">
            {proName[0]}
          </div>
          <div>
            <div className="text-xs text-gray-500">Requesting quote from</div>
            <div className="font-bold text-navy text-sm">{proName}</div>
          </div>
        </div>
      )}

      <div className="flex-1 px-5 py-5 overflow-y-auto">
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-500 text-sm rounded-2xl p-3 mb-4 animate-fade-in">
            {error}
          </div>
        )}

        {/* Step 0 - Job Details */}
        {step === 0 && (
          <div className="animate-slide-up space-y-5">
            <div>
              <label className="label mb-3">Job Size</label>
              <div className="space-y-2">
                {sizes.map(size => (
                  <button key={size.label} onClick={() => setJobSize(size.label)}
                    className={"w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border-2 transition-all text-left " +
                      (jobSize === size.label ? "bg-mouna-blue border-mouna-blue" : "bg-white border-gray-200 hover:border-gray-300")}>
                    <div className={"w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 " +
                      (jobSize === size.label ? "border-white" : "border-gray-300")}>
                      {jobSize === size.label && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
                    </div>
                    <div>
                      <div className={"font-bold text-sm " + (jobSize === size.label ? "text-white" : "text-navy")}>
                        {size.label}
                      </div>
                      <div className={"text-xs " + (jobSize === size.label ? "text-blue-200" : "text-gray-400")}>
                        {size.desc}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="label">Describe the Work</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                rows={4} placeholder="Tell the pro exactly what you need done..."
                className="input resize-none"/>
              <div className="text-xs text-gray-400 mt-1 text-right">{description.length}/500</div>
            </div>

            <div>
              <label className="label mb-3">Urgency</label>
              <div className="grid grid-cols-3 gap-2">
                {URGENCY_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setUrgency(opt.value)}
                    className={"rounded-2xl p-3 border-2 transition-all text-center " +
                      (urgency === opt.value ? "bg-mouna-blue border-mouna-blue" : "bg-white border-gray-200")}>
                    <div className="text-xl mb-1">{opt.icon}</div>
                    <div className={"font-bold text-xs " + (urgency === opt.value ? "text-white" : "text-navy")}>
                      {opt.label}
                    </div>
                    <div className={"text-xs " + (urgency === opt.value ? "text-blue-200" : "text-gray-400")}>
                      {opt.desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 1 - Location & Time */}
        {step === 1 && (
          <div className="animate-slide-up space-y-4">
            <div className="form-group">
              <label className="label">Service Address</label>
              <textarea value={address} onChange={e => setAddress(e.target.value)}
                rows={2} placeholder="123 Main St, Houston, TX 77001"
                className="input resize-none"/>
            </div>

            <div className="form-group">
              <label className="label">Preferred Date & Time</label>
              <input type="datetime-local" value={preferredDate}
                onChange={e => setPreferredDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
                className="input"/>
            </div>

            <div className="form-group mb-0">
              <label className="label">Private Notes for Pro (optional)</label>
              <textarea value={privateNotes} onChange={e => setPrivateNotes(e.target.value)}
                rows={3} placeholder="Gate code, parking info, special instructions..."
                className="input resize-none"/>
              <div className="text-xs text-gray-400 mt-1">🔒 Only visible to your matched pro</div>
            </div>
          </div>
        )}

        {/* Step 2 - Photos */}
        {step === 2 && (
          <div className="animate-slide-up">
            <div className="bg-blue-pale rounded-2xl p-4 mb-5">
              <div className="font-bold text-navy text-sm mb-1">📸 Add Photos (Optional)</div>
              <div className="text-xs text-gray-500">Photos help pros give you a more accurate quote</div>
            </div>

            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {photoPreviews.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                    <img src={p} alt="" className="w-full h-full object-cover"/>
                    <button onClick={() => {
                      setPhotos(prev => prev.filter((_, idx) => idx !== i));
                      setPhotoPreviews(prev => prev.filter((_, idx) => idx !== i));
                    }} className="absolute top-1.5 right-1.5 bg-red-500 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center shadow">
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label htmlFor="photo-upload"
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-200 rounded-2xl py-10 cursor-pointer hover:border-mouna-blue hover:bg-blue-pale transition-all">
              <span className="text-4xl">📷</span>
              <div className="text-center">
                <div className="font-bold text-navy text-sm">Tap to add photos</div>
                <div className="text-xs text-gray-400 mt-0.5">JPG, PNG, or video up to 50MB</div>
              </div>
            </label>
            <input id="photo-upload" type="file" accept="image/*,video/*" multiple className="hidden"
              onChange={e => {
                const files = Array.from(e.target.files || []);
                setPhotos(prev => [...prev, ...files]);
                files.forEach(file => {
                  const reader = new FileReader();
                  reader.onload = ev => setPhotoPreviews(prev => [...prev, ev.target?.result as string]);
                  reader.readAsDataURL(file);
                });
              }}/>

            {photos.length === 0 && (
              <button onClick={handleSubmit} disabled={loading}
                className="btn btn-ghost btn-full mt-4 text-gray-400">
                Skip & Submit Without Photos
              </button>
            )}
          </div>
        )}
      </div>

      {/* Bottom button */}
      <div className="px-5 pb-8 pt-4 bg-white border-t border-gray-100">
        <button onClick={handleNext} disabled={loading}
          className="btn btn-primary btn-full btn-lg">
          {loading ? "Sending..." :
           step === STEPS.length - 1 ? "Send Quote Request 🚀" :
           `Continue → ${STEPS[step + 1].label}`}
        </button>
      </div>
    </div>
  );
}
